import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, MoreHorizontal, BellOff, Bell, Shield, Flag, Check, CheckCheck, Clock3, AlertCircle } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ActionMenu } from '@/components/ui/ActionMenu';
import { toast } from '@/lib/toast';
import { format, isToday, isYesterday, isSameDay, differenceInMinutes } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { fetchP2PThreadInit, insertChatMessage, updateChatParticipant } from '@/queries/chat';
import { useAuth } from '@/hooks/useAuth';
import { useUserBlocks } from '@/hooks/useUserBlocks';
import { cn } from '@/lib/utils';
import { AuthSheet } from '@/components/auth/AuthSheet';
import { ChatComposer } from './ChatComposer';
import { ComposerActionSheet } from './ComposerActionSheet';
import { MessageAttachment } from './MessageAttachment';
import { ItemPin } from './ItemPin';
import { InlineOfferCard } from './InlineOfferCard';
import { InlineMeetupCard } from './InlineMeetupCard';
import { InlineRatingCard } from './InlineRatingCard';
import { MakeOfferSheet } from './MakeOfferSheet';
import { ProposeMeetupSheet } from './ProposeMeetupSheet';
import { useThreadItemContext } from '@/hooks/useThreadItemContext';
import { ReportDialog } from '@/components/social/ReportDialog';
import type { ChatMessage } from '@/hooks/useTeamConversation';

interface P2PThreadProps {
	conversationId: string;
	otherUserId?: string | null;
	otherUsername: string | null;
	otherAvatarUrl: string | null;
	onBack: () => void;
}

type SendStatus = 'sending' | 'sent' | 'failed';
type ExtMessage = ChatMessage & { metadata?: any; _status?: SendStatus };

/**
 * 1:1 P2P conversation with FB-Messenger-style features:
 * sticky item pin, inline offer / counter / pickup / rating cards,
 * bubble grouping, day separators, realtime seen indicator,
 * block / mute / report.
 */
export const P2PThread = ({
	conversationId,
	otherUserId,
	otherUsername,
	otherAvatarUrl,
	onBack,
}: P2PThreadProps) => {
	const { user } = useAuth();
	const { isBlockedEitherWay, block, unblock, iBlocked } = useUserBlocks();
	const [messages, setMessages] = useState<ExtMessage[]>([]);
	const [loading, setLoading] = useState(true);
	const [muted, setMuted] = useState(false);
	const [otherLastReadAt, setOtherLastReadAt] = useState<string | null>(null);
	const [showReport, setShowReport] = useState(false);
	const [ratingSnoozedUntil, setRatingSnoozedUntil] = useState<string | null>(null);
	const [actionSheetOpen, setActionSheetOpen] = useState(false);
	const [offerSheet, setOfferSheet] = useState<{ open: boolean; counterTo?: { interestId: string; previousCents: number } | null }>({ open: false });
	const [meetupSheetOpen, setMeetupSheetOpen] = useState(false);
	const [authSheetOpen, setAuthSheetOpen] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const composerFileTriggerRef = useRef<() => void>(() => {});

	const blockedByMe = !!otherUserId && iBlocked.has(otherUserId);
	const blockedEitherWay = isBlockedEitherWay(otherUserId);

	// Derive current item being discussed from the messages stream
	const { current: pinnedItem } = useThreadItemContext(messages);

	// Load messages + participant state
	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoading(true);
			const init = await fetchP2PThreadInit({
				conversationId,
				userId: user?.id,
				otherUserId: otherUserId ?? null,
			});
			if (cancelled) return;
			setMessages(init.messages as ExtMessage[]);
			setMuted(!!init.myParticipant?.muted);
			setRatingSnoozedUntil(
				(init.myParticipant?.metadata?.rating_snoozed_until as string) ?? null,
			);
			setOtherLastReadAt(init.otherLastReadAt);
			setLoading(false);
		})();
		return () => {
			cancelled = true;
		};
	}, [conversationId, user?.id, otherUserId]);

	// Realtime — stable channel keyed on the conversation id (no random suffix,
	// which was leaking channels on every StrictMode remount).
	useEffect(() => {
		const channel = supabase
			.channel(`p2p:${conversationId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'chat_messages',
					filter: `conversation_id=eq.${conversationId}`,
				},
				(payload) => {
					const m = payload.new as ExtMessage;
					setMessages((prev) => {
						// Dedupe against real inserts already present.
						if (prev.some((x) => x.id === m.id)) return prev;
						// Reconcile with optimistic bubble from this session: match by
						// same sender + identical body within a 15s window.
						const mineFromMe = user?.id && m.sender_id === user.id;
						if (mineFromMe) {
							const t = new Date(m.created_at).getTime();
							const idx = prev.findIndex(
								(x) =>
									(x as any)._status === 'sending' &&
									x.sender_id === m.sender_id &&
									x.body === m.body &&
									Math.abs(new Date(x.created_at).getTime() - t) < 15_000,
							);
							if (idx !== -1) {
								const next = [...prev];
								next[idx] = m;
								return next;
							}
						}
						return [...prev, m];
					});
				},
			)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'chat_participants',
					filter: `conversation_id=eq.${conversationId}`,
				},
				(payload) => {
					const row = payload.new as any;
					if (otherUserId && row.user_id === otherUserId) {
						setOtherLastReadAt(row.last_read_at ?? null);
					}
				},
			)
			.subscribe();
		return () => {
			supabase.removeChannel(channel);
		};
	}, [conversationId, otherUserId, user?.id]);

	// Smart auto-scroll: smooth for own sends, auto for incoming while at bottom,
	// and don't scroll when the user has intentionally scrolled up.
	const lastOwnSendRef = useRef<number | null>(null);
	const lastMessageLengthRef = useRef(messages.length);
	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const prevLength = lastMessageLengthRef.current;
		lastMessageLengthRef.current = messages.length;
		const grew = messages.length > prevLength;
		if (!grew) return;

		const last = messages[messages.length - 1];
		const isOwnSend = last?.sender_id === user?.id;
		const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
		const wasOwnSend = lastOwnSendRef.current === prevLength;
		lastOwnSendRef.current = isOwnSend ? messages.length : lastOwnSendRef.current;

		if (nearBottom || isOwnSend) {
			const behavior = isOwnSend || wasOwnSend ? 'smooth' : 'auto';
			el.scrollTo({ top: el.scrollHeight, behavior });
		}
	}, [messages, user?.id]);

	// Find an accepted interest in this thread (enables "Propose pickup")
	const acceptedInterestId = useMemo(() => {
		for (let i = messages.length - 1; i >= 0; i--) {
			const m = messages[i] as any;
			if (m.kind === 'offer_accepted' && m.metadata?.interest_id) return m.metadata.interest_id as string;
		}
		return null;
	}, [messages]);

	// Pending rating: find completed transactions between us with no review from me yet
	const [pendingRating, setPendingRating] = useState<
		{ transactionId: string; counterpartyId: string } | null
	>(null);

	useEffect(() => {
		if (!user || !otherUserId) return;
		const snoozed =
			ratingSnoozedUntil && new Date(ratingSnoozedUntil).getTime() > Date.now();
		if (snoozed) return;
		let cancelled = false;
		(async () => {
			// Look for any completed transaction between these two users
			const { data: txs } = await supabase
				.from('transactions')
				.select('id, seller_id, buyer_id, status, created_at')
				.in('status', ['completed'])
				.or(
					`and(seller_id.eq.${user.id},buyer_id.eq.${otherUserId}),and(seller_id.eq.${otherUserId},buyer_id.eq.${user.id})`
				)
				.order('created_at', { ascending: false })
				.limit(5);
			if (cancelled || !txs?.length) return;

			const txIds = txs.map((t: any) => t.id);
			const { data: existing } = await supabase
				.from('reviews')
				.select('transaction_id')
				.in('transaction_id', txIds)
				.eq('reviewer_id', user.id);
			const reviewedIds = new Set((existing ?? []).map((r: any) => r.transaction_id));
			const target = txs.find((t: any) => !reviewedIds.has(t.id));
			if (target) setPendingRating({ transactionId: target.id, counterpartyId: otherUserId });
		})();
		return () => {
			cancelled = true;
		};
	}, [user, otherUserId, ratingSnoozedUntil, messages.length]);

	const handleSend = async ({
		body,
		attachmentPaths,
	}: {
		body: string;
		attachmentPaths: string[];
	}) => {
		if (!user) return;
		if (blockedEitherWay) {
			toast.error('Messaging unavailable', { description: 'This thread is blocked.' });
			return;
		}
		// Optimistic bubble: renders immediately with a "sending" glyph,
		// gets reconciled by the realtime INSERT above (or flipped to failed).
		const tempId = `optim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
		const optimistic: ExtMessage = {
			id: tempId,
			conversation_id: conversationId,
			sender_id: user.id,
			kind: 'user' as any,
			body,
			attachments: attachmentPaths,
			created_at: new Date().toISOString(),
			_status: 'sending',
		} as ExtMessage;
		setMessages((prev) => [...prev, optimistic]);

		const { error } = await insertChatMessage({
			conversationId,
			senderId: user.id,
			kind: 'user',
			body,
			attachments: attachmentPaths,
		});
		if (error) {
			console.error('[P2PThread] send failed', error);
			setMessages((prev) =>
				prev.map((m) => (m.id === tempId ? ({ ...m, _status: 'failed' } as ExtMessage) : m)),
			);
			toast.error('Could not send', { description: error.message });
		}
	};

	const handleToggleMute = async () => {
		if (!user) return;
		const next = !muted;
		setMuted(next);
		const { error } = await updateChatParticipant(conversationId, user.id, { muted: next });
		if (error) {
			setMuted(!next);
			toast.error('Could not update');
			return;
		}
		toast.success(next ? 'Muted' : 'Unmuted');
	};

	const handleBlockToggle = async () => {
		if (!otherUserId) return;
		try {
			if (blockedByMe) {
				await unblock(otherUserId);
				toast.success('Unblocked');
			} else {
				await block(otherUserId);
				toast.success('Blocked', { description: 'They can no longer message you.' });
			}
		} catch (err: any) {
			toast.error('Could not update', { description: err?.message });
		}
	};

	const snoozeRating = async () => {
		if (!user) return;
		const until = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
		setRatingSnoozedUntil(until);
		setPendingRating(null);
		await updateChatParticipant(conversationId, user.id, {
			metadata: { rating_snoozed_until: until },
		});
	};

	// Seen indicator: last own message older than other's last_read_at
	const lastSeenMineId = useMemo(() => {
		if (!user || !otherLastReadAt) return null;
		const t = new Date(otherLastReadAt).getTime();
		const mine = messages.filter(
			(m) => m.sender_id === user.id && new Date(m.created_at).getTime() <= t
		);
		return mine.length > 0 ? mine[mine.length - 1].id : null;
	}, [messages, otherLastReadAt, user]);

	const initial = otherUsername?.charAt(0).toUpperCase() ?? '?';

	const renderMessage = (m: ExtMessage, prev: ExtMessage | undefined) => {
		const mine = !!m.sender_id && !!user && m.sender_id === user.id;
		const kind = m.kind as string;

		// Pure system text (legacy)
		if (kind === 'system') {
			return (
				<div key={m.id} className="text-center text-[11px] text-muted-foreground/70 py-1">
					{m.body}
				</div>
			);
		}

		// Item-card pin message (centered, compact)
		if (kind === 'item_card') {
			return (
				<div key={m.id} className="flex justify-center py-1">
					<div className="w-full max-w-[82%]">
						<ItemPin
							itemId={m.metadata?.item_id}
							title={m.metadata?.item_title}
							subtitle="Discussing this item"
							variant="pin"
						/>
					</div>
				</div>
			);
		}

		// Offer-family cards
		if (
			kind === 'offer' ||
			kind === 'counter_offer' ||
			kind === 'offer_accepted' ||
			kind === 'offer_declined' ||
			kind === 'offer_withdrawn'
		) {
			return (
				<div
					key={m.id}
					className={cn('flex items-end gap-2', mine ? 'justify-end' : '')}
				>
					{!mine && <AvatarBubble src={otherAvatarUrl} initial={initial} />}
					<InlineOfferCard
						mine={mine}
						conversationId={conversationId}
						messageKind={kind as any}
						metadata={m.metadata}
						currentUserId={user?.id}
						onCounter={(interestId, prevCents) =>
							setOfferSheet({ open: true, counterTo: { interestId, previousCents: prevCents } })
						}
					/>
				</div>
			);
		}

		if (kind === 'meetup') {
			return (
				<div
					key={m.id}
					className={cn('flex items-end gap-2', mine ? 'justify-end' : '')}
				>
					{!mine && <AvatarBubble src={otherAvatarUrl} initial={initial} />}
					<InlineMeetupCard
						mine={mine}
						conversationId={conversationId}
						metadata={m.metadata}
						currentUserId={user?.id}
					/>
				</div>
			);
		}

		// Bubble grouping: hide avatar if prev message is from same sender within 5min
		const groupedWithPrev =
			prev &&
			prev.sender_id === m.sender_id &&
			(prev.kind === 'user') &&
			Math.abs(differenceInMinutes(new Date(m.created_at), new Date(prev.created_at))) < 5;

		return (
			<div key={m.id} className="space-y-0.5">
				<div className={cn('flex items-end gap-2', mine ? 'justify-end' : '')}>
					{!mine &&
						(groupedWithPrev ? (
							<div className="w-6 shrink-0" />
						) : (
							<AvatarBubble src={otherAvatarUrl} initial={initial} />
						))}
					<div
						className={cn(
							'max-w-[82%] rounded-2xl px-4 py-2.5 text-[14px] leading-snug whitespace-pre-wrap break-words',
							mine
								? 'bg-primary text-primary-foreground rounded-br-md'
								: 'bg-white/[0.05] text-foreground rounded-bl-md'
						)}
					>
						{m.body}
						{m.attachments && m.attachments.length > 0 && (
							<div className="mt-2 flex flex-wrap gap-2">
								{m.attachments.map((path) => (
									<MessageAttachment key={path} bucket="chat-attachments" path={path} />
								))}
							</div>
						)}
					</div>
				</div>
				{mine && (
					(() => {
						const status = (m as any)._status as SendStatus | undefined;
						if (status === 'sending') {
							return (
								<div className="flex items-center justify-end gap-1 pr-1 text-[10.5px] text-muted-foreground/60">
									<Clock3 className="h-3 w-3" />
									<span>Sending…</span>
								</div>
							);
						}
						if (status === 'failed') {
							return (
								<div className="flex items-center justify-end gap-1 pr-1 text-[10.5px] text-destructive">
									<AlertCircle className="h-3 w-3" />
									<span>Failed — tap to retry</span>
								</div>
							);
						}
						if (lastSeenMineId === m.id) {
							return (
								<div className="flex items-center justify-end gap-1 pr-1 text-[10.5px] text-active-gold/80">
									<CheckCheck className="h-3.5 w-3.5" />
									<span>Seen</span>
								</div>
							);
						}
						// Sent: single check on the most recent mine bubble only, to avoid clutter.
						const laterMine = messages.slice(messages.indexOf(m) + 1).some((x) => x.sender_id === user?.id);
						if (!laterMine) {
							return (
								<div className="flex items-center justify-end gap-1 pr-1 text-[10.5px] text-muted-foreground/60">
									<Check className="h-3.5 w-3.5" />
									<span>Sent</span>
								</div>
							);
						}
						return null;
					})()
				)}
			</div>
		);
	};

	// Build render list with day separators
	const renderList: React.ReactNode[] = [];
	let lastDate: Date | null = null;
	messages.forEach((m, i) => {
		const d = new Date(m.created_at);
		if (!lastDate || !isSameDay(lastDate, d)) {
			renderList.push(
				<div key={`sep-${m.id}`} className="text-center text-[10.5px] text-muted-foreground/60 py-3 font-medium uppercase tracking-wide">
					{isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'EEE, MMM d')}
				</div>
			);
		}
		lastDate = d;
		renderList.push(renderMessage(m, messages[i - 1]));
	});

	return (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="px-3 pt-2.5 pb-2.5 flex items-center gap-2 shrink-0 border-b border-white/[0.05]">
				<button
					type="button"
					onClick={onBack}
					aria-label="Back to inbox"
					className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors -ml-1"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
				<Avatar className="h-9 w-9">
					<AvatarImage src={otherAvatarUrl ?? undefined} />
					<AvatarFallback className="text-xs">{initial}</AvatarFallback>
				</Avatar>
				<div className="flex-1 min-w-0">
					<div className="text-[13px] font-semibold text-foreground leading-tight truncate">
						{otherUsername ?? 'Collector'}
					</div>
					<div className="text-[11px] text-muted-foreground leading-tight">
						{muted ? 'Muted' : 'Direct message'}
					</div>
				</div>
				<ActionMenu
					align="end"
					trigger={
						<button
							type="button"
							aria-label="More"
							className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/[0.05] transition-colors -mr-1"
						>
							<MoreHorizontal className="h-4 w-4" />
						</button>
					}
					items={[
						{
							key: 'mute',
							label: muted ? 'Unmute' : 'Mute notifications',
							icon: muted ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />,
							onSelect: handleToggleMute,
						},
						...(otherUserId
							? [{
								key: 'block',
								label: blockedByMe ? 'Unblock user' : 'Block user',
								icon: <Shield className="h-5 w-5" />,
								onSelect: handleBlockToggle,
							}]
							: []),
						{
							key: 'report',
							label: 'Report',
							icon: <Flag className="h-5 w-5" />,
							destructive: true,
							separatorBefore: true,
							onSelect: () => setShowReport(true),
						},
					]}
				/>
			</div>

			{/* Sticky "Now discussing" pin */}
			{pinnedItem && (
				<div className="px-3 pt-2 pb-1 shrink-0">
					<ItemPin
						itemId={pinnedItem.id}
						title={pinnedItem.title}
						imageUrl={pinnedItem.image_url}
						priceCents={pinnedItem.asking_price_cents}
						hasProofPack={pinnedItem.has_proof_pack}
						subtitle="Now discussing"
						variant="pin"
					/>
				</div>
			)}

			{/* Messages */}
			<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
				{loading && (
					<div className="text-center text-[12px] text-muted-foreground/70">Loading…</div>
				)}
				{!loading && messages.length === 0 && (
					<div className="text-center text-[12px] text-muted-foreground/70 py-8">
						Say hi to {otherUsername ?? 'this collector'}.
					</div>
				)}

				{renderList}

				{pendingRating && (
					<div className="pt-2">
						<InlineRatingCard
							transactionId={pendingRating.transactionId}
							reviewedUserId={pendingRating.counterpartyId}
							reviewedUserName={otherUsername ?? 'this collector'}
							onDismiss={snoozeRating}
						/>
					</div>
				)}
			</div>

			{/* Composer */}
			{blockedEitherWay ? (
				<div className="shrink-0 px-4 py-3 border-t border-white/[0.05] flex flex-col items-center gap-2">
					<div className="text-[12px] text-muted-foreground text-center">
						{blockedByMe
							? 'You blocked this user. Unblock to resume messaging.'
							: 'You can no longer message this user.'}
					</div>
					{blockedByMe && (
						<button
							type="button"
							onClick={handleBlockToggle}
							className="inline-flex items-center gap-1.5 rounded-full bg-active-gold px-4 py-2 text-[12.5px] font-bold text-[#121416] hover:brightness-110 transition-all"
						>
							Unblock
						</button>
					)}
				</div>
			) : !user ? (
				<div className="shrink-0 px-4 py-4 border-t border-white/[0.05] flex flex-col items-center gap-2">
					<div className="text-[12.5px] text-muted-foreground text-center">
						Sign in to reply to {otherUsername ?? 'this collector'}.
					</div>
					<button
						type="button"
						onClick={() => setAuthSheetOpen(true)}
						className="inline-flex items-center gap-1.5 rounded-full bg-active-gold px-4 py-2 text-[12.5px] font-bold text-[#121416] hover:brightness-110 transition-all"
					>
						Sign in to reply
					</button>
				</div>
			) : (
				<ChatComposer
					disabled={!user}
					placeholder="Type a message…"
					allowAttachments
					bucket="chat-attachments"
					pathPrefix={conversationId}
					onSend={handleSend}
					onPlusClick={() => setActionSheetOpen(true)}
					registerFileTrigger={(fn) => (composerFileTriggerRef.current = fn)}
				/>
			)}

			{/* Action sheets */}
			<ComposerActionSheet
				open={actionSheetOpen}
				onOpenChange={setActionSheetOpen}
				canOffer={!!pinnedItem?.id}
				canMeetup={!!acceptedInterestId}
				onMakeOffer={() => setOfferSheet({ open: true, counterTo: null })}
				onProposeMeetup={() => setMeetupSheetOpen(true)}
				onAttachPhoto={() => composerFileTriggerRef.current?.()}
				hint={pinnedItem?.title ? `Discussing: ${pinnedItem.title}` : undefined}
			/>

			<MakeOfferSheet
				open={offerSheet.open}
				onOpenChange={(open) => setOfferSheet({ open, counterTo: open ? offerSheet.counterTo : null })}
				conversationId={conversationId}
				itemId={pinnedItem?.id}
				itemTitle={pinnedItem?.title}
				currentAskingCents={pinnedItem?.asking_price_cents ?? null}
				currentUserId={user?.id}
				counterTo={offerSheet.counterTo}
				sellerUsername={otherUsername}
			/>

			<ProposeMeetupSheet
				open={meetupSheetOpen}
				onOpenChange={setMeetupSheetOpen}
				conversationId={conversationId}
				currentUserId={user?.id}
				acceptedInterestId={acceptedInterestId}
				itemTitle={pinnedItem?.title}
			/>

			<ReportDialog
				open={showReport}
				onOpenChange={setShowReport}
				reportedUserId={otherUserId ?? undefined}
				targetLabel={otherUsername ?? 'this user'}
			/>
			<AuthSheet open={authSheetOpen} onOpenChange={setAuthSheetOpen} />
		</div>
	);
};

const AvatarBubble = ({ src, initial }: { src: string | null | undefined; initial: string }) => (
	<Avatar className="h-6 w-6 shrink-0">
		<AvatarImage src={src ?? undefined} />
		<AvatarFallback className="text-[10px]">{initial}</AvatarFallback>
	</Avatar>
);

export default P2PThread;
